import { createQueryKeys } from '@lukemorales/query-key-factory';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HttpService } from '@/features/api/http.service';
import {
  invalidateWorkspaceMarkers,
  workspacesKeyFactory,
} from '@/features/workspaces/services/workspace.api';
import { deepMerge } from '@/libs/utils/deepMerge';
import { toastBackendError } from '@/features/api/utils/toastBackendError';
import type { IPagination, IProject } from '@/types/api';
import type {
  IReqDeleteProjectDTO,
  IResDeleteProjectDTO,
} from '@/types/dto/project/IDeleteProjectDTO';

// TODO: temporary, need to handle pagination
export interface ProjectPaginated {
  projects: IProject[];
  pagination: IPagination;
}

const PROJECTS_PATH = '/projects';

const PROJECTS_FACTORY_NAME = 'projects';

export const projectsKeyFactory = createQueryKeys(PROJECTS_FACTORY_NAME, {
  getAll: (page: number) => ({
    queryKey: [PROJECTS_PATH, page],
    queryFn: () => {
      return HttpService.get<string, ProjectPaginated>(
        `${PROJECTS_PATH}`,
        new URLSearchParams({ page: page.toString() }),
      );
    },
  }),
  getProject: (id: string) => ({
    queryKey: [PROJECTS_PATH, id],
    queryFn: () => HttpService.get<string, ProjectPaginated>(`${PROJECTS_PATH}/${id}`),
  }),
});

export async function invalidAllProjectQueries(queryClient: QueryClient) {
  await queryClient.invalidateQueries({
    queryKey: [PROJECTS_FACTORY_NAME],
  });
}

export function useProjectsQuery(page: number) {
  return useQuery(projectsKeyFactory.getAll(page));
}

export function useProjectQuery(id: string) {
  return useQuery(projectsKeyFactory.getProject(id));
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      updatedProject,
    }: {
      projectId: string;
      updatedProject: Partial<IProject>;
      page: number;
    }) =>
      HttpService.patch<Partial<IProject>, IProject>(
        `${PROJECTS_PATH}/${projectId}`,
        updatedProject,
      ),
    // Optimistic update: reflect the edit in the list cache immediately
    // (the WORKSPACE cell must update without waiting for the round trip) and
    // roll back on failure.
    onMutate: async ({ projectId, updatedProject, page }) => {
      const listKey = projectsKeyFactory.getAll(page).queryKey;

      // 1. Cancel in-flight list fetches to avoid clobbering optimistic state.
      await queryClient.cancelQueries({ queryKey: listKey });

      // 2. Snapshot the page for rollback.
      const previousList = queryClient.getQueryData<ProjectPaginated>(listKey);

      // 3. Optimistically deep-merge the patch into the matching row.
      if (previousList) {
        queryClient.setQueryData<ProjectPaginated>(listKey, {
          pagination: previousList.pagination,
          projects: previousList.projects.map((project) =>
            project.id === projectId ? deepMerge(project, updatedProject) : project,
          ),
        });
      }

      const previousWorkspaceId = previousList?.projects.find(
        (project) => project.id === projectId,
      )?.workspace_id;

      return { previousList, listKey, previousWorkspaceId };
    },
    onError: (err, _variables, context) => {
      // Roll back to the pre-mutation snapshot.
      if (context?.previousList) {
        queryClient.setQueryData(context.listKey, context.previousList);
      }
      toastBackendError(err);
    },
    onSuccess: (updatedProject, variables, context) => {
      // Reconcile the optimistic row with the server's authoritative response
      // (e.g. workspace_name derived server-side from workspace_id).
      queryClient.setQueryData(
        projectsKeyFactory.getAll(variables.page).queryKey,
        (oldPaginatedResp: ProjectPaginated) => {
          return {
            pagination: oldPaginatedResp.pagination,
            projects: oldPaginatedResp.projects.map((project) =>
              project.id === variables.projectId ? deepMerge(project, updatedProject) : project,
            ),
          };
        },
      );

      // The workspace's cached data (its record and its map pins) still
      // reflects the pre-assignment state, so a row click straight to
      // /map?workspace=<id> would serve stale data until a hard reload.
      const newWorkspaceId = updatedProject.workspace_id;
      if (newWorkspaceId) {
        void invalidateWorkspaceMarkers(queryClient, newWorkspaceId);
        void queryClient.invalidateQueries({
          queryKey: workspacesKeyFactory.getOne(newWorkspaceId).queryKey,
        });
      }
      const previousWorkspaceId = context?.previousWorkspaceId;
      if (previousWorkspaceId && previousWorkspaceId !== newWorkspaceId) {
        void invalidateWorkspaceMarkers(queryClient, previousWorkspaceId);
      }

      toast.success('The project was updated');
    },
    onSettled: (_data, _error, variables) => {
      // Eventual-consistency backstop: two concurrent same-page cell edits can
      // make onMutate's cancelQueries drop the first edit's onSuccess result, so
      // reconcile the page against the server once the mutation settles.
      queryClient.invalidateQueries({
        queryKey: projectsKeyFactory.getAll(variables.page).queryKey,
      });
    },
  });
}

export interface IDeleteBatchProjectVars extends IReqDeleteProjectDTO {
  /** Workspace ids whose location-markers cache must be invalidated (one per deleted project; duplicates and nulls are fine). */
  workspace_ids: Array<string | null | undefined>;
}

// Optimistic update on all project pages
export function useDeleteBatchProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ project_ids }: IDeleteBatchProjectVars) =>
      HttpService.delete<IReqDeleteProjectDTO, IResDeleteProjectDTO>(`${PROJECTS_PATH}`, {
        project_ids,
      }),
    onMutate: async ({ project_ids: projectIds }: IDeleteBatchProjectVars) => {
      // 1. Stop outgoing fetches to avoid overwriting optimistic data
      await queryClient.cancelQueries({ queryKey: [PROJECTS_FACTORY_NAME] });

      // 2. Snapshot *all cached pages* for rollback
      const previousPages = queryClient
        .getQueriesData<ProjectPaginated>({
          queryKey: [PROJECTS_FACTORY_NAME],
        })
        .map(([key, data]) => ({ key, data }));

      // 3. Optimistically remove all IDs from every cached page
      for (const { key, data } of previousPages) {
        if (!data) continue;

        queryClient.setQueryData<ProjectPaginated>(key, {
          ...data,
          projects: data.projects.filter((p) => !projectIds.includes(p.id)),
        });
      }

      // Return snapshot for rollback
      return { previousPages };
    },
    onError: (err, _vars, context) => {
      // 4. Rollback to previous state
      if (context?.previousPages) {
        for (const { key, data } of context.previousPages) {
          queryClient.setQueryData(key, data);
        }
      }
      toastBackendError(err);
    },
    onSuccess: (_data, variables) => {
      // 5. Background refresh (does not cause double refetch)
      queryClient.invalidateQueries({
        queryKey: [PROJECTS_FACTORY_NAME],
        refetchType: 'inactive',
      });

      // 6. Their pins must disappear from the map too.
      const workspaceIds = new Set(variables.workspace_ids.filter((id): id is string => !!id));
      for (const workspaceId of workspaceIds) {
        void invalidateWorkspaceMarkers(queryClient, workspaceId);
      }

      toast.success('The project was removed');
    },
  });
}

export function useCancelProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      HttpService.delete<Partial<IProject>, IProject>(`${PROJECTS_PATH}/${projectId}`),
    onSuccess: async () => {
      await invalidAllProjectQueries(queryClient);
      toast.success('The project was canceled');
    },
    onError: (err) => {
      toastBackendError(err);
    },
  });
}
